import { Link } from "@tanstack/react-router";
import { Phone, MessageCircle, Calendar, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

interface Props {
  /** Which guide this banner is on — passed into tracking + WhatsApp prefill. */
  guideSlug: string;
  /** Service the guide relates to — used for the booking CTA params. */
  service?: string;
  /** Headline override. */
  headline?: string;
  /** Optional subtext override. */
  subtext?: string;
}

/**
 * Strong mid-guide CTA banner. Inserted between guide sections and the FAQ
 * so a reader who's just absorbed the advice has a phone-first next step
 * without scrolling further. Every interaction is tracked so we can see which
 * guides actually convert.
 */
export function GuideEnquiryBanner({ guideSlug, service, headline, subtext }: Props) {
  const biz = getBusinessConfig();
  const meta: Record<string, string> = { guide: guideSlug, location: "guide_banner" };
  if (service) meta.service = service;
  const wa = service
    ? `https://wa.me/${biz.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`I just read your ${guideSlug} guide and need a quote for ${service}. Reg: `)}`
    : biz.whatsappUrl;

  return (
    <aside className="my-10 md:my-12 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-6 md:p-8">
      <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">
            Need a hand from a real garage?
          </p>
          <h3 className="mt-2 text-xl md:text-2xl font-bold text-foreground leading-snug">
            {headline ?? "Tell us the reg — we'll quote your exact price in under a minute"}
          </h3>
          <p className="mt-2 text-sm md:text-base text-muted-foreground max-w-2xl">
            {subtext ??
              "DVSA-authorised MOT station in Poplar, E14 — five minutes from Canary Wharf. Honest written quotes, no scare tactics, no booking fees."}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row md:flex-col gap-2 md:min-w-[220px]">
          <a
            href={biz.phoneHref}
            onClick={() => trackEvent("call_click", meta)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent("whatsapp_click", meta)}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ea952]"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </a>
          <Link
            to="/contact"
            onClick={() => trackEvent("service_cta_click", meta)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
          >
            Send details <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/book"
            onClick={() => trackEvent("booking_request_start", meta)}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm font-bold text-foreground hover:border-primary/40"
          >
            <Calendar className="h-4 w-4" /> Book online
          </Link>
        </div>
      </div>
    </aside>
  );
}
